import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { FetchReturn, IAssemblyTag } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const useGetAllAssemblyTags = (
  userRole?: AuthClassification | null,
  filters: string[] = [],
  enableApi: boolean = true,
) =>
  useQuery({
    queryKey: queryKeys.assemblyTags.all(filters),
    queryFn: () => fetchAllAssemblyTags(filters),
    enabled: enableApi,
  });

export const useGetAllAssemblyTagsWithPagination = (filters: string[] = []) => {
  return useInfiniteQuery({
    queryKey: queryKeys.assemblyTags.pagination(filters),
    queryFn: ({ pageParam = 1 }) =>
      fetchAllAssemblyTags(concat(filters, [`page=${pageParam}`]), true),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

const fetchAllAssemblyTags = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IAssemblyTag[]>> => {
  const url = `/assemblyTags${generateQuery(
    [],
    concat(filters, isPagination ? '' : 'pageSize=0'),
  )}`;

  const response = await requestCCApi({ url });

  if (!isPagination) {
    return response.data as FetchReturn<T, IAssemblyTag[]>;
  } else {
    return {
      data: response.data,
      totalPages: response.totalPages ?? 0,
      totalCount: response.totalCount ?? 0,
    } as FetchReturn<T, IAssemblyTag[]>;
  }
};
